import React, { Fragment, ReactElement, ReactNode, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./TabList.scss";
import {TabItemProps, TabKey, TabListProps} from "./Tabs.model";
import AdminComponent from "../../auth/AdminComponent/AdminComponent";
import { LoginMenu } from "../../auth/LoginMenu/LoginMenu";
import {Breadcrumb} from "rsuite";
import {MdArrowRightAltIcon} from "../../../models/Icons.model";

const isTabElement = (child: ReactNode): child is ReactElement<TabItemProps> => {
  if (!React.isValidElement(child)) return false;
  const props = child.props as TabItemProps;
  return Boolean(props.label && props.tabKey);
};

const TabList = ({ children, activeTabKey, onTabChange }: TabListProps) => {
  const navigate = useNavigate();
  const visibleTabs = useMemo(
    () => React.Children.toArray(children).filter(isTabElement),
    [children]
  );

  const currentKey = useMemo(() => {
    const keys = visibleTabs.map((t) => t.props.tabKey) as TabKey[];
    if (!activeTabKey || !keys.includes(activeTabKey)) {
      return keys[0];
    }
    return activeTabKey;
  }, [activeTabKey, visibleTabs]);

  useEffect(() => {
    if (!onTabChange || !visibleTabs.length) return;

    const keys = visibleTabs.map((t) => t.props.tabKey) as TabKey[];
    if (!activeTabKey || !keys.includes(activeTabKey)) {
      onTabChange(keys[0]);
    }
  }, [activeTabKey, visibleTabs, onTabChange]);

  const handleTabClick = (tabKey: TabKey) => {
    if (onTabChange) {
      onTabChange(tabKey);
    }
  };

  const handleUsersClick = () => {
    navigate("/users");
  };

  const activeTabElement =
    visibleTabs.find((t) => t.props.tabKey === currentKey) || visibleTabs[0];

  return (
    <Fragment>

      <Breadcrumb className="mb-2" separator={<MdArrowRightAltIcon size={16} />} aria-label="breadcrumb" >
        <Breadcrumb.Item style={{fontSize: 16}}>Разделы</Breadcrumb.Item>
        <Breadcrumb.Item style={{fontSize: 16}}>{activeTabElement?.props?.label || ""}</Breadcrumb.Item>
      </Breadcrumb>

      <div className="tabs">
        <div className="row">
          <div className="sidebar col-md-3 col-lg-2 p-0 d-none d-md-block" style={{ zIndex: 1 }}>
            <div
              className="offcanvas-md offcanvas-end"
              tabIndex={-1}
              id="tabsSidebarMenu"
              aria-labelledby="tabsSidebarMenuLabel"
            >
              <div className="offcanvas-header">
                <h5 className="offcanvas-title" id="tabsSidebarMenuLabel">
                  Разделы
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="offcanvas"
                  data-bs-target="#tabsSidebarMenu"
                  aria-label="Close"
                ></button>
              </div>
              <div className="offcanvas-body d-md-flex flex-column p-0 pt-lg-3 overflow-y-auto">
                <nav className="tab-list-wrapper">
                  <ul className="tab-list" role="tablist" aria-orientation="horizontal">
                    {visibleTabs.map((tab) => {
                      const key = tab.props.tabKey as TabKey;
                      const isActive = key === currentKey;
                      return (
                        <li key={`tab-${key}`}>
                          <button
                            data-bs-dismiss="offcanvas"
                            data-bs-target="#tabsSidebarMenu"
                            aria-label="Close"
                            onClick={() => handleTabClick(key)}
                            className={`btn tab-btn ${isActive ? "active" : ""}`}
                          >
                            {tab.props.label}
                          </button>
                        </li>
                      );
                    })}
                    <AdminComponent>
                      {() => (
                        <li className="d-md-none">
                          <button
                            type="button"
                            onClick={handleUsersClick}
                            data-bs-dismiss="offcanvas"
                            data-bs-target="#tabsSidebarMenu"
                            className="btn tab-btn tab-action-btn"
                          >
                            Пользователи
                          </button>
                        </li>
                      )}
                    </AdminComponent>
                    <LoginMenu
                      showLabel
                      hideIcon
                      className="btn tab-btn tab-action-btn tab-logout-btn d-md-none"
                      itemClassName="d-md-none"
                    />
                  </ul>
                </nav>
              </div>
            </div>
          </div>
          <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4">
            {activeTabElement}
          </main>
        </div>
      </div>
    </Fragment>
  );
};

export default TabList;
